import {ActionType, FeatureStatus} from '@utils/feature_tracking'
import FeatureTrackingBuilder from '@utils/feature_tracking_builder'
import FaqModule from '@general_inquiry/FaqModule.ts';
import * as tracking from '@utils/tracking.ts';
import MessageEntryStep from '@general_inquiry/MessageEntryStep.ts';
import MessageEntryStepCustomerLoggedIn from '@general_inquiry/MessageEntryStepCustomerLoggedIn.ts';

class GeneralInquiryStartPage {
    private static readonly TOP_PARENT_FEATURE_ID = 'contactform_contactchannels'
    private static readonly TOP_PARENT_FEATURE_NAME = 'ContactChannels'
    private static readonly CHANNEL_FEATURE_ID_PHONENUMBER_OTTO = '1'
    private static readonly CHANNEL_FEATURE_ID_CALLBACK = '2'
    private static readonly CHANNEL_FEATURE_ID_SHOW_PHONENUMBER_OTTO = '1'
    private static readonly CHANNEL_FEATURE_NAME = 'ContactChannel'
    private static readonly OTHER_TOPIC_ID = '50'
    private static readonly CHANNEL_FEATURE_ID_MESSAGE_FORM = '3'

    private static readonly SHOW_FAQ_ANSWERS_STEP_CSS_CLASS = 'con_focussed-dialog__faqanswersstep'
    private static readonly SHOW_FAQ_QUESTIONS_STEP_CSS_CLASS = 'con_focussed-dialog__faqquestionsstep'
    private static readonly SHOW_FAQ_TOPICCHOICE_STEP_CSS_CLASS = 'con_focussed-dialog__topicchoicestep'
    private static readonly SHOW_CONTACT_DATA_STEP_CSS_CLASS = 'con_focussed-dialog__contactdatastep'
    private static readonly PHONE_TRACKING_IDENTIFIER = 'phonenumber_otto'
    private static readonly CALLBACK_TRACKING_IDENTIFIER = 'callback'
    private static readonly MESSAGE_FORM_TRACKING_IDENTIFIER = 'contactform_otto'

    private readonly _windowObject: Window
    private readonly $documentObject: Document
    private readonly $focussedDialogContainer: HTMLElement | null
    private readonly $trackingDataHolder: HTMLElement | null
    private readonly $loginButton: HTMLAnchorElement | null
    private readonly phoneButtonChannel: ContactChannel =
        {
            id: GeneralInquiryStartPage.CHANNEL_FEATURE_ID_SHOW_PHONENUMBER_OTTO,
            label: 'show_phonenumber_otto',
            trackingAction: 'submitAction',
            selectorHook: 'con_generalinquiry__phone-channel'
        }
    private readonly contactChannels: Array<ContactChannel> = [
        {
            id: GeneralInquiryStartPage.CHANNEL_FEATURE_ID_PHONENUMBER_OTTO,
            label: GeneralInquiryStartPage.PHONE_TRACKING_IDENTIFIER,
            trackingAction: 'submitAction',
            selectorHook: 'con_generalinquiry__phone-channel'
        },
        {
            id: GeneralInquiryStartPage.CHANNEL_FEATURE_ID_CALLBACK,
            label: GeneralInquiryStartPage.CALLBACK_TRACKING_IDENTIFIER,
            trackingAction: 'submitMoveAction',
            selectorHook: 'con_generalinquiry__callback-channel'
        },
        {
            id: GeneralInquiryStartPage.CHANNEL_FEATURE_ID_MESSAGE_FORM,
            label: GeneralInquiryStartPage.MESSAGE_FORM_TRACKING_IDENTIFIER,
            trackingAction: 'click',
            selectorHook: 'con_generalinquiry__message-channel'
        }
    ]

    private readonly $topicChoiceStep: HTMLDivElement | null
    private readonly $topicChoiceStepSubjectElements: NodeListOf<HTMLDivElement> | null
    private readonly $faqQuestionsStep: HTMLDivElement | null
    private readonly $faqQuestionsStepBackButton: HTMLButtonElement | null
    private readonly $faqQuestionsTopicHeadline: HTMLDivElement | null
    private readonly $faqQuestionsContactLink: HTMLDivElement | null
    private readonly $faqAnswersStep: HTMLDivElement | null
    private readonly $faqAnswersStepBackButton: HTMLButtonElement | null
    private readonly $contactDataStepBackButton: HTMLDivElement | null
    private readonly $phoneButton: HTMLDivElement | null
    private readonly $phoneButtonText: HTMLDivElement | null
    private readonly $otherTopicSubject: HTMLDivElement | null
    private readonly $messaggeEntryStepButton: HTMLButtonElement | null
    private readonly $showFaqs: boolean
    private readonly $phoneNumberShown: string
    private readonly $contactPhoneNumber: string
    private $isPhoneNumberShown: boolean = false
    private $topicClickedId: string = '';
    private $generalBackButton: HTMLButtonElement | null
    private isUserLoggedIn: boolean | undefined;
    private $successPageCustomerInboxButton: HTMLButtonElement | null

    private constructor(windowObject: Window, documentObject: Document) {
        this._windowObject = windowObject
        this.$documentObject = documentObject
        this.$focussedDialogContainer = documentObject.querySelector('[data-js-hook="con_focussed-dialog__container"]')
        this.$trackingDataHolder = this.$focussedDialogContainer!.querySelector('[data-js-hook="con_generalinquiry__tracking-data-holder"]')
        this.$loginButton = this.$focussedDialogContainer!.querySelector('[data-js-hook="con_generalinquiry__login-button"]')
        this.$topicChoiceStep = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-topicchoicestep"]') || null
        this.$topicChoiceStepSubjectElements = this.$focussedDialogContainer?.querySelectorAll('[data-js-hook="con_generalinquiry-topicchoicestep__subject"]') || null
        this.$faqQuestionsStep = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep"]') || null
        this.$faqQuestionsStepBackButton = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__back-button"]') || null
        this.$faqQuestionsTopicHeadline = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question"]') || null
        this.$faqQuestionsContactLink = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__contact-link"]') || null
        this.$faqAnswersStep = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqanswersstep"]') || null
        this.$faqAnswersStepBackButton = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-faqanswersstep__back-button"]') || null
        this.$contactDataStepBackButton = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-start__back-button"]') || null
        this.$showFaqs = !!this.$topicChoiceStep
        this.$phoneButton = this.$focussedDialogContainer?.querySelector(`[data-js-hook="con_generalinquiry__phone-channel"]`) || null
        this.$contactPhoneNumber = this.$phoneButton?.dataset?.jsContactPhoneNumber ?? ''
        this.$phoneNumberShown = this.$phoneButton?.dataset?.jsContactPhoneNumberShown ?? ''
        this.$phoneButtonText = this.$phoneButton?.querySelector(`[data-js-hook="con_generalinquiry__phone-channel__text"]`) || null
        this.$otherTopicSubject = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-topicchoicestep__subject-other"]') || null
        this.$generalBackButton = this.$focussedDialogContainer?.querySelector('[data-js-hook="con_generalinquiry-topicchoicestep__back-button"]') || null
        this.$messaggeEntryStepButton = this.$documentObject?.querySelector('[data-js-hook="con_generalinquiry__message-channel"]') || null
        this.$successPageCustomerInboxButton = this.$documentObject?.querySelector('[data-js-hook="con_generalinquiry-start__success-customer-inbox-button"]') || null
    }

    private trackContactSelectionPageImpression(): void {
        const parentFeature = this.createParentFeature(FeatureStatus.LOADED)
        const channelFeatures = this.createChannelFeatures()
        const payload = [parentFeature, ...channelFeatures]

        tracking.addFeaturesPageImpressionToTrackingServer(payload);
    }

    private registerClickTracking(): void {
        const parentFeature = this.createParentFeature(FeatureStatus.CLICKED)
        const labels = this.$trackingDataHolder ? {contact_Flow: this.getTrackingValueFromTrackingDataHolder()} : {}
        this.contactChannels
            .filter(channel => this.isChannelVisibleInPage(channel))
            .map(visibleChannel => this.toChannelAndLink(visibleChannel, parentFeature))
            .forEach(channelAndLink => this.addButtonClickAndTrack(labels, channelAndLink, parentFeature))

        if (this.$loginButton) {
            this.$loginButton.addEventListener('click', (event: MouseEvent) => {
                event.preventDefault()
                tracking.submitMoveToTrackingServer({...labels, contact_Activity: 'login'})
                this._windowObject.location.href = this.$loginButton!.href
            })
        }

        if (this.$topicChoiceStepSubjectElements) {
            Array.from(this.$topicChoiceStepSubjectElements).forEach(
                (subjectElement) => subjectElement.addEventListener('click', () => {
                    const eventData = {
                        contact_Activity: 'selectTopic_generalQuestion',
                        ot_PageCluster: 'Kontaktformulare',
                        contact_Topic: subjectElement.dataset.trackingValue,
                    }
                    const fullTrackingData = {...eventData}
                    tracking.sendEventToTrackingServer(fullTrackingData)
                    this.$topicClickedId = <string>subjectElement.dataset?.trackingValue
                    if (this.$topicClickedId === GeneralInquiryStartPage.OTHER_TOPIC_ID) {
                        this.getContactSelectionForm(this.$otherTopicSubject!.dataset.jsContactSelection)
                    }
                })
            )
        }
    }

    private toChannelAndLink(visibleChannel: ContactChannel, parentFeature: object) {
        const fullPayload = {
            name: ActionType.CLICK,
            features: [parentFeature, this.createChannelFeatureWithStatus(visibleChannel, FeatureStatus.CLICKED)]
        }
        const channelAndLink: ChannelAndLink = {
            payload: fullPayload,
            /*                                                                              */
            link: this.$focussedDialogContainer?.querySelector(`[data-js-hook="${visibleChannel.selectorHook}"]`)!,
            trackingAction: visibleChannel.trackingAction,
            label: visibleChannel.label
        }
        return channelAndLink
    }

    private addButtonClickAndTrack(labels: { contact_Flow: string } | object, channelAndLink: ChannelAndLink
        , parentFeature: object) {
        channelAndLink.link.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault()
            if (channelAndLink.label === GeneralInquiryStartPage.PHONE_TRACKING_IDENTIFIER && !this.$isPhoneNumberShown) {
                this.showPhoneNumber();
                const trackingPayload = this.toChannelAndLink(this.phoneButtonChannel, parentFeature)
                this.trackClickChannelEvent(trackingPayload, labels);
            } else {
                this.trackClickChannelEvent(channelAndLink, labels);
                this._windowObject.location.href = channelAndLink.link.href
            }
        });
    }

    private showPhoneNumber() {
        this.$phoneButtonText!.textContent = this.$phoneNumberShown;
        this.$phoneButton?.setAttribute('href', `tel:${this.$contactPhoneNumber}`)
        this.$isPhoneNumberShown = true;
    }

    private trackClickChannelEvent(channelAndLink: ChannelAndLink, labels: { contact_Flow: string } | object) {
        if (channelAndLink.trackingAction === 'submitAction') {
            tracking.sendEventWithActionToTrackingServer(labels, channelAndLink.payload)
        }
        if (channelAndLink.trackingAction === 'submitMoveAction') {
            tracking.submitMoveWithActionToTrackingServer(labels, channelAndLink.payload)
        }
        if (channelAndLink.trackingAction === 'click') {
            this.trackingMessageEntryStep()
            labels = {contact_Flow: this.isUserLoggedIn ? 'loggedin_noSelection' : 'loggedout_noOrders'}
            tracking.sendEventWithActionToTrackingServer(labels, channelAndLink.payload)
        }
    }

    private registerFaqQuestionsClickOnContactLinkTracking() {
        if (this.$faqQuestionsStep) {
            this.$faqQuestionsContactLink && this.$faqQuestionsContactLink.addEventListener('click', () => {
                const eventData = {
                    contact_Activity: 'contact_gen_question_click',
                }
                tracking.sendEventToTrackingServer(eventData)
                this.getContactSelectionForm(this.$faqQuestionsContactLink!.dataset.jsContactSelection)
            })
        }
    }

    private createParentFeature(featureStatus: FeatureStatus): object {
        const featureTrackingBuilder = new FeatureTrackingBuilder(
            GeneralInquiryStartPage.TOP_PARENT_FEATURE_ID,
            GeneralInquiryStartPage.TOP_PARENT_FEATURE_NAME,
            featureStatus)
        return featureTrackingBuilder.build().generateFeatureTrackingPayload()
    }

    private createChannelFeatures(): Array<any> {
        const channelFeatures: any[] = []
        this.contactChannels.forEach((channel) => {
            const displayStatus: FeatureStatus = this.isChannelVisibleInPage(channel) ? FeatureStatus.VISIBLE : FeatureStatus.HIDDEN
            const features = this.createChannelFeatureWithStatus(channel, displayStatus)
            channelFeatures.push(...[features])
        })
        return channelFeatures
    }

    private isChannelVisibleInPage(channel: ContactChannel) {
        return !!this.$focussedDialogContainer?.querySelector(`[data-js-hook="${channel.selectorHook}"]`);
    }

    private createChannelFeatureWithStatus(channel: ContactChannel, featureStatus: FeatureStatus) {
        return new FeatureTrackingBuilder(
            channel.id,
            GeneralInquiryStartPage.CHANNEL_FEATURE_NAME,
            featureStatus)
            .parentId(GeneralInquiryStartPage.TOP_PARENT_FEATURE_ID)
            .labels({contact_Channel: [channel.label]})
            .build()
            .generateFeatureTrackingPayload()
    }

    private initStepMaker(): void {
        if (this.$focussedDialogContainer) {
            this.setIsUserLoggedIn()
            addEventListener('hashchange', () => {
                this.displayStepFromUri()
            }, false)
            this.$topicChoiceStepSubjectElements && Array.from(this.$topicChoiceStepSubjectElements).forEach(
                (button) => {
                    if (button.getAttribute('data-js-subject-value') !== 'OTHER') {
                        button.addEventListener('click', () => this.gotoStep('#faqquestionsstep'))
                    }
                }
            )
            const allBackButtons = [
                this.$faqQuestionsStepBackButton,
                this.$faqQuestionsTopicHeadline,
                this.$faqAnswersStepBackButton,
                this.$contactDataStepBackButton,
                this.$generalBackButton
            ]
            allBackButtons.forEach(button => {
                button && button.addEventListener('click', () => this._windowObject.history.back())
            })

            if (this.getStepFromUri() == '#contactdatastep' || (this.getStepFromUri() == '#topicchoicestep' && this.$showFaqs)) {
                this.displayStepFromUri()
            } else if (this.$showFaqs) {
                /*                                                           */
                this.gotoStep('#topicchoicestep')
            } else {
                /*                                                */
                this.gotoStep('#contactdatastep')
            }
        }
    }

    private bindStepMakingFromFaqQuestionToFaqAnswer(): void {
        if (this.$faqQuestionsStep) {
            this.$faqQuestionsStep.querySelectorAll('[data-js-hook="con_generalinquiry-faqquestionsstep__question-item"]').forEach(questionItem => {
                questionItem.addEventListener('click', () => this.gotoStep('#faqanswersstep'))
            })
        }
    }

    private gotoStep(step: GeneralInquiryStep): void {
        this._windowObject.location.hash = step
    }

    private getStepFromUri(): GeneralInquiryStep {
        if (availableGeneralInquirySteps.indexOf(<GeneralInquiryStep>this._windowObject.location.hash) > -1) {
            if (!this.$topicClickedId || this.$topicClickedId.trim() === '') {
                this._windowObject.location.hash = '#topicchoicestep';
                return '#topicchoicestep';
            }
            return <GeneralInquiryStep>this._windowObject.location.hash
        } else {
            return this.$showFaqs ? '#topicchoicestep' : '#contactdatastep'
        }
    }

    private displayStepFromUri(): void {
        const currentStep = this.getStepFromUri()
        if (currentStep == '#faqquestionsstep') {
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_TOPICCHOICE_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_ANSWERS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_CONTACT_DATA_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.add(GeneralInquiryStartPage.SHOW_FAQ_QUESTIONS_STEP_CSS_CLASS)
        } else if (currentStep == '#faqanswersstep') {
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_TOPICCHOICE_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_QUESTIONS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_CONTACT_DATA_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.add(GeneralInquiryStartPage.SHOW_FAQ_ANSWERS_STEP_CSS_CLASS)
        } else if (currentStep == '#topicchoicestep') {
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_QUESTIONS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_ANSWERS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_CONTACT_DATA_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.add(GeneralInquiryStartPage.SHOW_FAQ_TOPICCHOICE_STEP_CSS_CLASS)
        } else if (currentStep == '#contactdatastep') {
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_TOPICCHOICE_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_QUESTIONS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.remove(GeneralInquiryStartPage.SHOW_FAQ_ANSWERS_STEP_CSS_CLASS)
            this.$focussedDialogContainer?.classList.add(GeneralInquiryStartPage.SHOW_CONTACT_DATA_STEP_CSS_CLASS)
            if (!this.isMessageEntryStepPageVisible()) {
                this.trackContactSelectionPageImpression()
            }
        }
    }

    private getContactSelectionForm(href: string | undefined): void {
        if (href) {
            const url = new URL(href, this._windowObject.location.origin);
            const currentUrl = new URL(this._windowObject.location.href);
            const focussedDialogCloseUrl = currentUrl.searchParams.get('focussedDialogCloseUrl');
            if (focussedDialogCloseUrl) {
                url.searchParams.append('focussedDialogCloseUrl', focussedDialogCloseUrl);
            }
            if (this.$topicClickedId && this.$topicClickedId.trim() !== '') {
                url.searchParams.append('topicId', this.$topicClickedId);
            }
            this._windowObject.location.href = url.toString();
        }
    }

    private initFaqModule() {
        this.$topicChoiceStepSubjectElements && FaqModule.initialize(
            this._windowObject,
            this.$documentObject,
            this.$topicChoiceStepSubjectElements,
            this.bindStepMakingFromFaqQuestionToFaqAnswer.bind(this))
    }

    private initMessageEntryStep() {
        MessageEntryStep.initialize(this.$documentObject)
    }

    private initMessageEntryStepCustomerLoggedIn() {
        MessageEntryStepCustomerLoggedIn.initialize(this.$documentObject)
    }

    static initialize(windowObject: Window, documentObject: Document): GeneralInquiryStartPage {
        const generalInquiryStartPage: GeneralInquiryStartPage = new GeneralInquiryStartPage(windowObject, documentObject)
        generalInquiryStartPage.setIsUserLoggedIn()
        generalInquiryStartPage.initStepMaker()
        generalInquiryStartPage.initFaqModule()
        generalInquiryStartPage.registerClickTracking()
        generalInquiryStartPage.registerFaqQuestionsClickOnContactLinkTracking()
        generalInquiryStartPage.initMessageEntryStep()
        generalInquiryStartPage.initMessageEntryStepCustomerLoggedIn()
        generalInquiryStartPage.initSuccessPageTrackingButtons()
        return generalInquiryStartPage
    }

    private getTrackingValueFromTrackingDataHolder(): string {
        /*                                                                              */
        return this.$trackingDataHolder?.dataset.jsTrackingdataContactFlow!
    }

    private trackingMessageEntryStep(): void {
        if (this.$messaggeEntryStepButton) {
            const eventData = {
                action: 'click',
                contact_Flow: this.isUserLoggedIn ? 'loggedin_noSelection' : 'loggedout_noOrders',
                name: 'ContactChannels',
                status: 'clicked',
                contact_Channel: 'contactform_otto',
            }
            tracking.sendEventToTrackingServer(eventData)
        }
    }

    private isMessageEntryStepPageVisible(): boolean {
        return !!this.$documentObject.querySelector('[data-js-hook="con_generalinquiry-start__form-content"]');
    }

    private setIsUserLoggedIn() {
        const input = this.$documentObject.querySelector('[data-js-hook="con_generalinquiry-start__is-user-logged-in"]');
        this.isUserLoggedIn = input?.getAttribute('value') === 'true';
    }

    private initSuccessPageTrackingButtons(): void {
        this.$successPageCustomerInboxButton && this.$successPageCustomerInboxButton.addEventListener('click', () => {
            const eventData = {
                contact_InboxInteraction: 'click',
                contact_InboxInteractionName: 'ToInboxOverview',
                contact_InboxInteractionSource: 'loggedInGeneralInquiryContactFlow'
            }
            tracking.sendEventToTrackingServer(eventData)
            const link = this.$successPageCustomerInboxButton?.querySelector('a');
            const href = link?.getAttribute('href');
            if (href) {
                window.location.href = href;
            }
        })
    }
}

export default GeneralInquiryStartPage

interface ContactChannel {
    id: string
    label: string
    trackingAction: string
    selectorHook: string
}

interface ChannelAndLink {
    payload: any
    link: HTMLAnchorElement
    trackingAction: string
    label: string
}

const availableGeneralInquirySteps = ['#topicchoicestep', '#faqquestionsstep', '#faqanswersstep', '#contactdatastep'] as const
type GeneralInquiryStep = typeof availableGeneralInquirySteps[number]
